import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  toggleOpen: () => void;
}
const FAQItem = ({
  question,
  answer,
  isOpen,
  toggleOpen
}: FAQItemProps) => {
  return <div className="border border-blue-100 rounded-lg mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-77">
      <button className="flex justify-between items-center w-full p-6 text-left bg-white focus:outline-none" onClick={toggleOpen} data-id="element-78">
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-79">{question}</h3>
        <div className="text-blue-600" data-id="element-80">
          {isOpen ? <ChevronUpIcon className="h-5 w-5" data-id="element-81" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-82" />}
        </div>
      </button>
      {isOpen && <div className="px-6 pb-6 text-gray-600 bg-white" data-id="element-83">
          {typeof answer === 'string' ? <p data-id="element-84">{answer}</p> : answer}
        </div>}
    </div>;
};
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const faqs = [{
    question: 'What types of content do you write?',
    answer: 'I specialize in a variety of content types including blog posts, SEO articles, website copy, product descriptions, email newsletters, white papers, case studies, social media content, and more. My versatility allows me to adapt to different tones, styles, and industries based on your specific needs.'
  }, {
    question: 'How do you charge for your services?',
    answer: 'My pricing structure depends on the type of content, complexity, research required, and turnaround time. I typically charge per word or per project. For regular clients, I offer package deals with discounted rates. Please contact me directly for a customized quote based on your specific requirements.'
  }, {
    question: 'What is your turnaround time?',
    answer: 'Turnaround time varies depending on the scope and complexity of the project. For standard blog posts (1000-1500 words), I typically deliver within 2-3 business days. Larger projects like comprehensive website copy or white papers may take 5-7 business days. Rush delivery is available for an additional fee, subject to my current schedule.'
  }, {
    question: 'Do you offer revisions to your work?',
    answer: "Yes, I include up to two rounds of revisions with every project to ensure you're completely satisfied with the final content. Additional revisions can be arranged for a nominal fee. My goal is to deliver content that perfectly matches your vision and requirements."
  }, {
    question: 'How do you research for industry-specific content?',
    answer: 'I conduct thorough research using credible sources, industry publications, academic journals, and competitor analysis. For specialized topics, I may interview subject matter experts or request specific materials from you. I prioritize accuracy and up-to-date information to ensure the content is both authoritative and valuable to your audience.'
  }, {
    question: 'Can you write content optimized for search engines (SEO)?',
    answer: "Absolutely! I'm well-versed in SEO best practices including keyword research, strategic keyword placement, meta descriptions, and creating content that satisfies search intent. I balance SEO requirements with engaging, reader-friendly content that drives both traffic and conversions."
  }, {
    question: 'What information do you need from me to get started?',
    answer: <div data-id="element-85">
          <p className="mb-3" data-id="element-86">
            To create the most effective content for your needs, I typically
            require:
          </p>
          <ul className="list-disc pl-5 space-y-2" data-id="element-87">
            <li data-id="element-88">Clear project brief outlining your goals and requirements</li>
            <li data-id="element-89">Target audience information</li>
            <li data-id="element-90">Brand voice and style guidelines</li>
            <li data-id="element-91">Preferred keywords (for SEO content)</li>
            <li data-id="element-92">Any specific points or topics you want covered</li>
            <li data-id="element-93">Examples of content you like</li>
            <li data-id="element-94">Access to relevant subject matter experts if needed</li>
          </ul>
        </div>
  }, {
    question: 'Do you provide content strategy services?',
    answer: 'Yes, beyond writing individual pieces, I can help develop a comprehensive content strategy tailored to your business goals. This includes content planning, editorial calendars, topic research, keyword strategy, and performance analysis to ensure your content marketing efforts yield the best possible results.'
  }, {
    question: 'How do we collaborate during the content creation process?',
    answer: "I maintain clear communication throughout the project via email, Telegram, WhatsApp, or your preferred platform. After receiving your brief, I'll confirm my understanding of the requirements. I'll provide updates as needed and deliver drafts for your review. Your feedback is incorporated during the revision process until you're completely satisfied with the final content."
  }, {
    question: 'Do you sign NDAs or confidentiality agreements?',
    answer: "Yes, I'm happy to sign Non-Disclosure Agreements (NDAs) or confidentiality agreements to protect your business information. I understand that content projects often involve sensitive details about your business strategy, upcoming products, or proprietary information, and I treat all client information with the utmost confidentiality."
  }];
  return <section id="faq" className="py-20 px-4 bg-blue-50" data-id="element-95">
      <div className="max-w-4xl mx-auto" data-id="element-96">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-16" data-id="element-97">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4" data-id="element-98">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} toggleOpen={() => toggleFAQ(index)} data-id="element-99" />)}
        </div>
      </div>
    </section>;
}